import axios from "axios";
import { SUPPORTED_MARKETPLACES } from "../config/marketplaces.js";
import { extractApiErrorMessage } from "./safeData.js";

const PLATFORM_HOST_HINTS = [
  ["amazon", "amazon."],
  ["shein", "shein."],
  ["temu", "temu."],
  ["aliexpress", "aliexpress."],
  ["ebay", "ebay."],
  ["walmart", "walmart."],
  ["alibaba", "alibaba."],
];

/**
 * Guess marketplace id from a product URL hostname.
 * @returns {string} A SUPPORTED_MARKETPLACES id, or "other".
 */
export function detectPlatformFromUrl(url) {
  const value = (url || "").trim();
  if (!value) return "other";

  try {
    const host = new URL(value).hostname.toLowerCase();
    for (const [platform, hint] of PLATFORM_HOST_HINTS) {
      if (host.includes(hint)) return platform;
    }
  } catch {
    return "other";
  }

  return "other";
}

export function isSupportedPlatform(platform) {
  return SUPPORTED_MARKETPLACES.some((entry) => entry.id === platform);
}

export function validateProductUrl(url) {
  const value = (url || "").trim();
  if (!value) return "Product URL is required";
  if (!/^https?:\/\//i.test(value)) return "URL must start with http:// or https://";
  try {
    // eslint-disable-next-line no-new
    new URL(value);
  } catch {
    return "Enter a valid product URL";
  }
  return "";
}

/**
 * Load title/images for a URL using the same preview chain as /submit.
 */
export async function previewProductUrl(url, apiBase) {
  const validationError = validateProductUrl(url);
  if (validationError) {
    return { success: false, error: validationError };
  }

  try {
    const importResponse = await axios.post(
      `${apiBase}/products/preview-from-url`,
      { url },
      { withCredentials: true }
    );
    const previewData = importResponse.data;
    if (previewData?.success) {
      return {
        success: true,
        platform: detectPlatformFromUrl(url),
        title: previewData.title || "",
        images: previewData.images?.length
          ? previewData.images
          : previewData.image
            ? [previewData.image]
            : [],
        category: previewData.category || "General",
        estimated_weight_lb: previewData.estimated_weight_lb ?? null,
        source_price: previewData.source_price ?? previewData.scrapedCost ?? null,
        landed_price: previewData.landed_price ?? previewData.dhcLandedPrice ?? null,
        pricing_model: previewData.pricing_model ?? null,
      };
    }
  } catch {
    /* fall through to link-preview */
  }

  try {
    const response = await axios.get(`${apiBase}/link-preview`, {
      params: { url },
      withCredentials: true,
    });
    if (response.data?.success) {
      return {
        success: true,
        platform: detectPlatformFromUrl(url),
        title: response.data.title || "",
        images: response.data.image ? [response.data.image] : [],
        category: "General",
        estimated_weight_lb: null,
      };
    }
    return {
      success: false,
      error: response.data?.error || "Could not preview this URL",
    };
  } catch (err) {
    return {
      success: false,
      error: extractApiErrorMessage(err, "Could not preview this URL"),
    };
  }
}

/**
 * POST /products/submit-url — lands in the admin pending queue as pending_review.
 */
export async function submitProductUrl(fields, apiBase, options = {}) {
  const url = (fields?.source_url || fields?.url || "").trim();
  const urlError = validateProductUrl(url);
  if (urlError) return { success: false, error: urlError };

  const platform = (fields?.source_platform || detectPlatformFromUrl(url)).trim().toLowerCase();
  if (!isSupportedPlatform(platform)) {
    return { success: false, error: "Unsupported marketplace for this URL" };
  }

  const title = (fields?.title || "").trim();
  if (title.length < 3) {
    return { success: false, error: "Product title is required (minimum 3 characters)" };
  }

  const images = Array.isArray(fields?.images)
    ? fields.images.filter((item) => typeof item === "string" && item.trim())
    : [];

  if (images.length === 0) {
    return { success: false, error: "Add at least one product image URL" };
  }

  const payload = {
    source_platform: platform,
    source_url: url,
    title,
    images,
    category: fields?.category || "General",
    estimated_weight_lb: fields?.estimated_weight_lb ?? null,
    reference_price: fields?.reference_price ?? null,
    currency: fields?.currency || "USD",
    variant_title: fields?.variant_title || null,
  };

  const endpoint = options.admin
    ? `${apiBase}/admin/products/submit-url`
    : `${apiBase}/products/submit-url`;

  if (options.admin) {
    if (fields?.customer_email?.trim()) {
      payload.customer_email = fields.customer_email.trim().toLowerCase();
    }
    if (fields?.profit_floor_override != null) {
      payload.profit_floor_override = fields.profit_floor_override;
    }
  }

  try {
    const response = await axios.post(endpoint, payload, {
      withCredentials: true,
    });
    return {
      success: true,
      productId: response.data?.product_id,
      status: response.data?.status || "pending_review",
      product: response.data,
      assignedCustomerEmail: response.data?.assigned_customer_email ?? null,
    };
  } catch (err) {
    return {
      success: false,
      error: extractApiErrorMessage(err, "Failed to submit product URL"),
    };
  }
}

/** Admin-only: submit on behalf of a customer or leave unassigned for review. */
export async function submitProductUrlAsAdmin(fields, apiBase) {
  return submitProductUrl(fields, apiBase, { admin: true });
}
